/**
 * <video-background-pause>
 * Wraps a background video (native <video> or Vimeo <iframe>) and:
 * 1. Pauses it when scrolled out of the viewport, resuming when back in view
 * 2. Never autoplays it at all if the user prefers reduced motion —
 *    falls back to the static poster image already in the markup instead
 */
class VideoBackgroundPause extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.iframe = this.querySelector('iframe');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    // No video/iframe present (e.g. background_type is 'image') — nothing to do
    if (!this.video && !this.iframe) return;

    if (this.reducedMotion) {
      // Hide the video layer entirely; the poster image behind it (rendered
      // by the section whenever an image is set) shows through instead
      this.classList.add('banner__video-wrapper--reduced-motion');
      if (this.video) this.video.pause();
      // No play command is ever sent to Vimeo, so nothing to pause there
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      root: null,
      threshold: 0,
    });
    this.observer.observe(this);

    if (this.iframe) {
      this.iframe.addEventListener('load', () => {
        this.vimeoReady = true;
      });
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.play();
      } else {
        this.pause();
      }
    });
  }

  play() {
    if (this.video) {
      this.video.play().catch(() => {});
    } else if (this.iframe && this.vimeoReady) {
      this.postToVimeo('play');
    }
  }

  pause() {
    if (this.video) {
      this.video.pause();
    } else if (this.iframe && this.vimeoReady) {
      this.postToVimeo('pause');
    }
  }

  postToVimeo(method) {
    if (!this.iframe.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      JSON.stringify({ method: method }),
      'https://player.vimeo.com'
    );
  }
}

customElements.define('video-background-pause', VideoBackgroundPause);